import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService, PermissionService } from '@abp/ng.core';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap } from 'rxjs/operators';
import { Popover, PopoverModule } from 'primeng/popover';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { CrmSegmentContactDto } from '../../models/crm-segment-contact.dto';
import { CrmSegmentDefinitionDto } from '../../models/crm-segment-definition.dto';
import { PendingContactsPopover } from '../../models/pending-contacts-popover.model';
import { SegmentContactRow } from '../../models/segment-contact-row.model';
import { CrmSegmentDefinitionService } from '../../services/crm-segment-definition.service';
import { toSegmentContactRow } from '../../utils/segment-contact-row.util';
import { CrmSegmentAssignedContactsComponent } from '../crm-segment-assigned-contacts/crm-segment-assigned-contacts.component';
import { CrmSegmentManageContactsDialogComponent } from '../crm-segment-manage-contacts-dialog/crm-segment-manage-contacts-dialog.component';

const ASSIGNED_CONTACTS_PAGE_SIZE = 20;

type SegmentTemplateContext = BaseListColumnTemplateContext<CrmSegmentDefinitionDto>;

@Component({
  selector: 'app-crm-segment-definitions-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    PopoverModule,
    ActionMenuComponent,
    BaseListComponent,
    CrmSegmentAssignedContactsComponent,
    CrmSegmentManageContactsDialogComponent,
  ],
  templateUrl: './crm-segment-definitions-list.component.html',
  styleUrl: './crm-segment-definitions-list.component.scss',
})
export class CrmSegmentDefinitionsListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly crmSegmentDefinitionService = inject(CrmSegmentDefinitionService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);
  private readonly permissionService = inject(PermissionService);

  @ViewChild('contactsPopover') contactsPopover!: Popover;
  @ViewChild('contactsTpl') contactsTpl!: TemplateRef<SegmentTemplateContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<SegmentTemplateContext>;

  @Input() reloadVersion = 0;

  @Output() edit = new EventEmitter<CrmSegmentDefinitionDto>();
  @Output() deleted = new EventEmitter<void>();

  segments: CrmSegmentDefinitionDto[] = [];
  loading = false;
  listConfig!: BaseListConfig<CrmSegmentDefinitionDto>;
  canEditSegments = this.permissionService.getGrantedPolicy(PermissionConstants.canEditSegments);
  selectedSegmentForManage: CrmSegmentDefinitionDto | null = null;
  showManageContactsDialog = false;

  contactsPopoverSegmentId: number | null = null;
  contactsPopoverOpen = false;
  popoverContacts: SegmentContactRow[] = [];
  popoverLoading = false;
  popoverLoadingMore = false;
  popoverTotalCount: number | null = null;

  private assignedContactsRequestId = 0;
  private pendingContactsPopover: PendingContactsPopover | null = null;

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadList();
    }
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<CrmSegmentDefinitionDto>[] = [
      { field: 'name', header: l('Settings::CRM:Definitions:Name') },
      {
        field: 'contactCount',
        header: l('Settings::CRM:Definitions:Contacts'),
        width: '140px',
        bodyTemplate: this.contactsTpl,
      },
      {
        field: 'id',
        header: l('Settings::CRM:Definitions:Actions'),
        width: '80px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
    };

    this.cdr.detectChanges();
  }

  getMenuItems(row: CrmSegmentDefinitionDto): ActionMenuItem[] {
    const items: ActionMenuItem[] = [];

    if (this.canEditSegments) {
      items.push({
        label: this.localization.instant('Settings::CRM:Definitions:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.edit.emit(row),
      });
      items.push({
        label: this.localization.instant('Settings::CRM:Definitions:ManageContacts'),
        icon: 'pi pi-users',
        action: () => this.onManageContacts(row),
      });
    }

    items.push({
      label: this.localization.instant('Settings::CRM:Definitions:Delete'),
      icon: 'pi pi-trash',
      severity: 'danger',
      action: () => this.onDelete(row),
    });

    return items;
  }

  onManageContacts(row: CrmSegmentDefinitionDto): void {
    this.selectedSegmentForManage = row;
    this.showManageContactsDialog = true;
  }

  onManageContactsSaved(): void {
    this.loadList();
  }

  onContactsClick(row: CrmSegmentDefinitionDto, event: Event): void {
    event.stopPropagation();

    if (this.contactsPopover.overlayVisible !== this.contactsPopoverOpen) {
      return;
    }

    const target = event.currentTarget as HTMLElement;

    if (this.contactsPopoverOpen) {
      this.pendingContactsPopover = this.contactsPopoverSegmentId === row.id ? null : { row, target };
      this.contactsPopover.hide();
      return;
    }

    this.openContactsPopover(row, target);
  }

  onContactsLoadMore(): void {
    const segmentId = this.contactsPopoverSegmentId;
    if (segmentId === null || this.popoverLoading || this.popoverLoadingMore) {
      return;
    }

    this.popoverLoadingMore = true;

    this.fetchAssignedContacts(segmentId, this.popoverContacts.length).subscribe({
      next: response => {
        this.popoverContacts = [...this.popoverContacts, ...(response.items ?? []).map(toSegmentContactRow)];
        this.popoverTotalCount = response.totalCount ?? this.popoverContacts.length;
      },
    });
  }

  onContactsPopoverShow(): void {
    this.contactsPopoverOpen = true;
  }

  onContactsPopoverHide(): void {
    this.contactsPopoverOpen = false;

    const pending = this.pendingContactsPopover;
    this.pendingContactsPopover = null;
    if (pending) {
      setTimeout(() => this.openContactsPopover(pending.row, pending.target));
    }
  }

  private openContactsPopover(row: CrmSegmentDefinitionDto, target: HTMLElement): void {
    this.contactsPopoverSegmentId = row.id;
    this.contactsPopover.show(null, target);

    this.popoverContacts = [];
    this.popoverTotalCount = null;
    this.popoverLoading = true;

    this.fetchAssignedContacts(row.id, 0).subscribe({
      next: response => {
        this.popoverContacts = (response.items ?? []).map(toSegmentContactRow);
        this.popoverTotalCount = response.totalCount ?? 0;
      },
      error: () => {
        this.popoverContacts = [];
        this.popoverTotalCount = 0;
      },
    });
  }

  private fetchAssignedContacts(
    segmentId: number,
    skipCount: number,
  ): Observable<ListResponseDto<CrmSegmentContactDto>> {
    const requestId = ++this.assignedContactsRequestId;

    return this.crmSegmentDefinitionService
      .getAssignedContactPage(segmentId, skipCount, ASSIGNED_CONTACTS_PAGE_SIZE)
      .pipe(
        filter(() => requestId === this.assignedContactsRequestId),
        finalize(() => {
          if (requestId !== this.assignedContactsRequestId) {
            return;
          }

          this.popoverLoading = false;
          this.popoverLoadingMore = false;
        }),
      );
  }

  private loadList(): void {
    this.loading = true;
    this.crudService.get<CrmSegmentDefinitionDto>(CrmSegmentDefinitionDto, null).subscribe({
      next: (response: ListResponseDto<CrmSegmentDefinitionDto>) => {
        this.segments = response.items ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private onDelete(segment: CrmSegmentDefinitionDto): void {
    this.crmSegmentDefinitionService
      .getLinkedContactCount(segment.id)
      .pipe(
        catchError(() => of(0)),
        switchMap(count => {
          const message =
            count > 0
              ? this.localization.instant(
                  'Settings::CRM:Definitions:DeleteConfirmMessage',
                  segment.name,
                  count.toString(),
                )
              : this.localization.instant(
                  'Settings::CRM:Definitions:DeleteUnusedConfirmMessage',
                  segment.name,
                );

          const title =
            count > 0
              ? 'Settings::CRM:Definitions:DeleteConfirmTitle'
              : 'Settings::CRM:Definitions:DeleteUnusedConfirmTitle';

          return this.confirmationService.warn(
            message,
            title,
            {
              hideCancelBtn: false,
              hideYesBtn: false,
              dismissible: true,
              yesText: 'Settings::CRM:Definitions:Delete',
              cancelText: 'AbpUi::Cancel',
            },
          );
        }),
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService
              .delete(CrmSegmentDefinitionDto, segment.id)
              .pipe(map(() => true));
          }
          return of(false);
        }),
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toasterService.success('::SuccessfullyDeleted');
          this.deleted.emit();
        },
      });
  }
}
